import { useAuthContext, useLogout } from '@pulse/auth-ui';
import type { ShellResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { FreshnessBanner, RefreshedLine } from '@/components/pulse/freshness';
import {
  InboxIcon,
  MilestonesIcon,
  PeopleIcon,
  ProjectsIcon,
  ReportsIcon,
  PulseMark,
  ShippedIcon,
  TalosIcon,
  TodayIcon,
  TrendsIcon,
} from '@/components/pulse/icons';
import { useMilestonesOverview } from '@/domains/milestones/api';
import { useShell } from '@/domains/shell/api';
import { NAV_SECTIONS, type NavSectionKey } from '@/domains/shell/constants';
import { NotOnTeamPanel } from '@/domains/users/not-on-team-panel';

const ICONS: Readonly<Record<NavSectionKey, () => React.ReactElement>> = {
  today: TodayIcon,
  inbox: InboxIcon,
  shipped: ShippedIcon,
  people: PeopleIcon,
  projects: ProjectsIcon,
  milestones: MilestonesIcon,
  reports: ReportsIcon,
  trends: TrendsIcon,
  talos: TalosIcon,
};

/**
 * The Pulse shell (SHL-001), built from the prototype's `.app` markup: the dark sidebar with the
 * brand, the seven sections and the signed-in member, and the page in `main`. At 900 px and under
 * the prototype's stylesheet turns the sidebar into a bar across the top.
 */
export function PulseShell(): React.ReactElement {
  const { pathname } = useLocation();
  const shell = useShell();
  // MS-6: milestones late or at risk, for the nav's badge. Only a member on the roster can read them.
  const milestones = useMilestonesOverview({ enabled: Boolean(shell.data?.member) });
  const signOut = useSignOut();
  const mainRef = React.useRef<HTMLElement>(null);

  // Moving between pages starts at the top, with focus on the page for screen readers.
  React.useEffect(() => {
    window.scrollTo(0, 0);
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <div className="pulse">
      <div className="app">
        <PulseNav pathname={pathname} shell={shell.data ?? null} milestonesAttention={milestones.data?.attention ?? null} onSignOut={signOut} />
        <main ref={mainRef} className="view" tabIndex={-1}>
          {shell.data !== undefined && shell.data.member === null ? (
            <NotOnTeamPanel signedInAs={shell.data.signedInAs} />
          ) : (
            <>
              <FreshnessBanner freshness={shell.data?.freshness ?? null} />
              <Outlet />
              <RefreshedLine freshness={shell.data?.freshness ?? null} />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * ACS-004 AC-7: signing out ends the session and returns to the sign-in page. The session is cleared
 * here even when the API can't be told (an expired token, say), so Sign out always works.
 */
function useSignOut(): () => void {
  const logout = useLogout();
  const { setToken } = useAuthContext();
  const navigate = useNavigate();
  return React.useCallback(() => {
    void logout
      .run()
      .catch(() => undefined)
      .finally(() => {
        setToken(null);
        navigate('/login', { replace: true });
      });
  }, [logout, setToken, navigate]);
}

/** The sidebar alone, so it can be rendered and checked without the network. */
export function PulseNav({
  pathname,
  shell,
  milestonesAttention = null,
  onSignOut,
}: {
  readonly pathname: string;
  readonly shell: ShellResponse | null;
  /** MS-6: open milestones late or at risk; null until known. */
  readonly milestonesAttention?: number | null;
  readonly onSignOut: () => void;
}): React.ReactElement {
  const inboxCount = shell?.inboxCount ?? null;
  const attention = milestonesAttention;
  return (
    <nav className="nav" aria-label="Main">
      <Link className="brand" to="/">
        <span className="brand-mark">
          <PulseMark />
        </span>
        <span className="stack">
          <span className="brand-name">Ten24 Pulse</span>
          <span className="brand-sub">Team reporting</span>
        </span>
      </Link>
      <div className="nav-links">
        {NAV_SECTIONS.map((section) => {
          const Icon = ICONS[section.key];
          return (
            <Link key={section.key} className="nav-link" to={section.to} aria-current={section.matches(pathname) ? 'page' : undefined}>
              <Icon />
              <span>{section.label}</span>
              {section.key === 'inbox' && inboxCount !== null ? (
                <b className="badge" aria-label={`${inboxCount} items`}>
                  {inboxCount}
                </b>
              ) : null}
              {section.key === 'milestones' && attention !== null && attention > 0 ? (
                <b className="badge" aria-label={`${attention} late or at risk`}>
                  {attention}
                </b>
              ) : null}
            </Link>
          );
        })}
      </div>
      <div className="nav-spacer" />
      <div className="me">
        {shell?.member ? (
          <span className="avatar sm" style={{ background: shell.member.avatarColour }}>
            {shell.member.initials}
          </span>
        ) : null}
        <span className="stack">
          <span style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{shell?.member?.displayName ?? shell?.signedInAs ?? ''}</span>
          {/* ACS-004 AC-7: Sign out shows even when the shell couldn't load, so nobody is stuck. */}
          <span style={{ fontSize: 12, color: 'var(--nav-muted)' }}>
            {shell === null ? '' : `${shell.member === null ? 'Not on the roster' : shell.roleLabel} · `}
            <button type="button" className="signout" onClick={onSignOut}>
              Sign out
            </button>
          </span>
        </span>
      </div>
    </nav>
  );
}
